import * as actionTypes from "../constants/actionTypes";
import * as componentTypes from "../constants/componentTypes";
import * as modelHelper from "../helpers/modelHelper";

const initialState = {
    active: componentTypes.DATABASE_SCENE,
    color: null,
    fandeck: null,
    product: null,
    formula: null,
    base: null,
    package: null,
    numberOfPackages: 1,
    moreInfoText: null,
    customer: null,
    lastCustomer: null,
    customNote: null,
    barcode: null,
    barcodeOrder: false,
    productList: [],
    colorList: [],
    fandeckList: [],
    packageList: [],
    customerList: [],
    historyOrder: null,
    from: new Date(new Date().setHours(0, 0, 0)),
    to: new Date(new Date().setHours(23, 59, 59))
};

/**
 * Structure of order store:
 * - active {String}
 * - color {Object}
 * - fandeck {Object}
 * - product {Object}
 * - base {Object}
 * - package {Object}
 * - numberOfPackages {Number}
 * - moreInfoText {String}
 * - customer {Object}
 * - lastCustomer {Object}      - lastCustomer object overrides customer object
 * - customNote {String}
 * - barcode {String | null}
 * - barcodeOrder {Boolean}
 * - productList {Array}
 * - colorList {Array}
 * - fandeckList {Array}
 * - packageList {Array}
 * - customerList {Array}
 * - historyOrder {Object}
 * - from {Object}
 * - to {Object}
 */
export const intergammaOrderReducer = (state = initialState, action) => {
    switch (action.type) {
        case actionTypes.INTERGAMMA_SET_OVERRIDE_MODE:
            return initialState;
        case actionTypes.INTERGAMMA_SELECT_DATABASE:
        case actionTypes.INTERGAMMA_SET_ACTIVE_DATABASE:
            return {
                ...state,
                active: componentTypes.DATABASE_SCENE,
                color: null,
                fandeck: null,
                product: null,
                formula: null,
                base: null,
                package: null,
                numberOfPackages: 1,
                moreInfoText: null,
                customer: null,
                customNote: null,
                barcode: null,
                barcodeOrder: false,
                productList: [],
                colorList: [],
                fandeckList: [],
                packageList: [],
                customerList: []
            };
        case actionTypes.INTERGAMMA_SAVE_COLOR_BY_BARCODE:
            return {
                ...state,
                color: action.payload.color,
                fandeck: null,
                product: null,
                formula: null,
                base: null,
                package: null,
                numberOfPackages: 1,
                moreInfoText: null,
                customer: null,
                customNote: null,
                barcode: null
            };
        case actionTypes.INTERGAMMA_SELECT_PACKAGE:
            return {
                ...state,
                package: action.payload.package
                    ? action.payload.package.updateImageId(state.package !== null ? state.package.imageId : -1)
                    : action.payload.package,
                customer: null,
                customNote: null,
                barcode: null
            };
        case actionTypes.INTERGAMMA_SAVE_PACKAGE_IMAGE:
            if (state.package === null) {
                return state;
            }

            return {
                ...state,
                package: state.package.updateImageId(action.payload.imageId)
            };
        case actionTypes.INTERGAMMA_RESET_COLOR:
            return {
                ...state,
                active: componentTypes.COLOR_SCENE,
                color: state.barcode === null ? state.color : null,
                fandeck: null,
                product: null,
                formula: null,
                base: null,
                package: null,
                numberOfPackages: 1,
                moreInfoText: null,
                customer: null,
                customNote: null,
                barcode: null
            };
        case actionTypes.INTERGAMMA_RESET_COLOR_WITH_FANDECK:
            return {
                ...state,
                active: componentTypes.COLOR_SCENE,
                color: state.barcode === null ? state.color : null,
                fandeck: state.fandeck !== null && state.fandeck.id !== 0 ? state.fandeck : null,
                product: null,
                formula: null,
                base: null,
                package: null,
                numberOfPackages: 1,
                moreInfoText: null,
                customer: null,
                customNote: null,
                barcode: null
            };
        case actionTypes.INTERGAMMA_RESET_FANDECK:
            return {
                ...state,
                active: componentTypes.FANDECK_SCENE,
                fandeck: state.barcode === null ? state.fandeck : null,
                product: null,
                formula: null,
                base: null,
                package: null,
                numberOfPackages: 1,
                moreInfoText: null,
                customer: null,
                customNote: null,
                barcode: null
            };
        case actionTypes.INTERGAMMA_RESET_FANDECK_WITH_COLOR:
            return {
                ...state,
                active: componentTypes.FANDECK_SCENE,
                color: state.color !== null && state.color.id !== 0 ? state.color : null,
                fandeck: state.barcode === null ? state.fandeck : null,
                formula: null,
                product: null,
                base: null,
                package: null,
                numberOfPackages: 1,
                moreInfoText: null,
                customer: null,
                customNote: null,
                barcode: null
            };
        case actionTypes.INTERGAMMA_RESET_PRODUCT:
            return {
                ...state,
                active: componentTypes.PRODUCT_SCENE,
                product: state.barcode === null ? state.product : null,
                formula: null,
                base: null,
                package: null,
                numberOfPackages: 1,
                moreInfoText: null,
                customer: null,
                customNote: null,
                barcode: null
            };
        case actionTypes.INTERGAMMA_RESET_PACKAGE:
            return {
                ...state,
                active: componentTypes.BIPP_SCENE,
                package: state.barcode === null ? state.package : null,
                moreInfoText: state.barcode === null ? state.moreInfoText : null,
                customer: null,
                customNote: null,
                barcode: null
            };
        case actionTypes.INTERGAMMA_RESET_NOTE:
            return {
                ...state,
                active: componentTypes.NOTE_SCENE
            };
        case actionTypes.INTERGAMMA_SAVE_PRODUCT_LIST:
            return {
                ...state,
                productList: modelHelper.mapSimpleProductsToObjects(action.payload.productList)
            };
        case actionTypes.INTERGAMMA_SAVE_RELOADED_FORMULA_LIST:
            return {
                ...state,
                colorList: state.colorList.concat(modelHelper.mapColorsToObjects(action.payload.colorList, false))
            };
        case actionTypes.INTERGAMMA_SAVE_RELOADED_FANDECK_LIST:
            return {
                ...state,
                fandeckList: state.fandeckList.concat(modelHelper.mapFandecksToObjects(action.payload.fandeckList))
            };
        case actionTypes.INTERGAMMA_SAVE_CUSTOMER_LIST:
            return {
                ...state,
                customerList: modelHelper.mapCustomersToObjects(action.payload.customerList)
            };
        case actionTypes.INTERGAMMA_CHANGE_NUMBER_OF_PACKAGES:
            return {
                ...state,
                numberOfPackages: action.payload.numberOfPackages
            };
        case actionTypes.INTERGAMMA_SAVE_ORDER_BARCODE:
            return {
                ...initialState,
                barcode: action.payload.barcode,
                barcodeOrder: true
            };
        case actionTypes.INTERGAMMA_RESET_ORDER_BARCODE:
        case actionTypes.MENU_ADD_NEW_OR_RESET_MAIN_TAB:
            return {
                ...initialState,
                barcode: null,
                barcodeOrder: false
            };
        case actionTypes.INTERGAMMA_SAVE_CUSTOM_NOTE:
            return {
                ...state,
                customNote: action.payload.note
            };
        case actionTypes.INTERGAMMA_CHANGE_DATE_FILTER:
            return {
                ...state,
                from: action.payload.from,
                to: action.payload.to
            };
        case actionTypes.INTERGAMMA_SAVE_COLOR_DATA:
            return {
                ...state,
                formula: state.formula.updateColorantAmounts(
                    action.payload.colorantListMl,
                    action.payload.colorantListG,
                    action.payload.colorantListShot
                )
            };
        case actionTypes.INTERGAMMA_RESET_LAST_CUSTOMER:
            return {
                ...state,
                lastCustomer: null
            };
        case actionTypes.INTERGAMMA_SET_LAST_CUSTOMER:
            return {
                ...state,
                lastCustomer: action.payload.customer
            };
        case actionTypes.WEBSOCKET_CLOSED:
            return initialState;
        default:
            return state;
    }
};
